import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Delete, EditNote, ExpandLess, ExpandMore, OpenInNew, PostAdd } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { asGermanDateTime } from 'src/common/date';
import { useAppDispatch } from 'src/redux-app-hooks';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';
import { isProblemDetailCode, isProblemDetailTextOr } from 'src/common/ProblemDetail';
import { ensanSteckbriefService } from '../ensan-steckbrief.service';
import { SteckbriefDto, SteckbriefVariantDto, variantDocumentNumber } from '../ensan-steckbrief.dto';
import { RealEstateAsset } from '../real-estate-asset-management.dto';
import { steckbriefVariantNewUrl, steckbriefVariantOpenUrl } from '../rea-page-params';
import { PRINT_LIMIT_STECKBRIEF_NAME } from '../steckbrief-variant-fields';

interface SteckbriefeTabProps {
    realEstateAsset: RealEstateAsset;
    /** Building select options shared with the other tabs — "(1/3) — address" labels. */
    buildingOptions: { value: string; label: string }[];
}

/**
 * Renders the "Steckbriefe" tab on the Real Estate Asset Management page
 * (RFC-004 §9, GEN-700/GEN-701, restructured in GEN-766): a table of the
 * selected REA's EnSan Steckbriefe (code, name, building subset) with a
 * name-only create dialog and per-row delete with confirmation. Creating
 * navigates straight into the routed Variant form page (GEN-765), where the
 * building subset is adjusted on the map. Each row expands into its Variant
 * list (mirroring the PV Analysis list page): name, kind, creator, date, the
 * generated-document link and — per Variant — an open-in-form action and a
 * soft delete with confirmation (the generated documents and the data stamp
 * survive server-side, GEN-763). The list has no inline data-stamp viewer:
 * opening the Variant's form shows its data. Document generation itself
 * lives in the Variant form page (GEN-765), not here.
 */
export function SteckbriefeTab({ realEstateAsset, buildingOptions }: SteckbriefeTabProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [steckbriefe, setSteckbriefe] = useState<SteckbriefDto[]>([]);
    const [variantsBySteckbriefId, setVariantsBySteckbriefId] = useState<Record<string, SteckbriefVariantDto[]>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [expandedSteckbriefId, setExpandedSteckbriefId] = useState<string | null>(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [createName, setCreateName] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const [deleteCandidate, setDeleteCandidate] = useState<SteckbriefDto | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [deleteVariantCandidate, setDeleteVariantCandidate] = useState<{
        steckbrief: SteckbriefDto;
        variant: SteckbriefVariantDto;
    } | null>(null);
    const [isDeletingVariant, setIsDeletingVariant] = useState(false);

    // Bumped after deletes to re-run the load effect below.
    const [reloadCount, setReloadCount] = useState(0);
    const reload = () => setReloadCount((count) => count + 1);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setLoadError(null);
        ensanSteckbriefService
            .getSteckbriefe(realEstateAsset.id, true)
            .then((data) => {
                // Variant lists arrive expanded in the list response (GEN-760). They
                // used to be fetched one request per Steckbrief, which Sentry flagged
                // as an N+1 on every page load (ADMI-FRONTEND-64).
                if (!cancelled) {
                    setSteckbriefe(data);
                    setVariantsBySteckbriefId(
                        Object.fromEntries(data.map((steckbrief) => [steckbrief.id, steckbrief.variants ?? []])),
                    );
                }
            })
            .catch(() => {
                if (!cancelled) setLoadError(t('Failed to load Steckbriefe.'));
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [realEstateAsset.id, reloadCount, t]);

    function buildingLabel(buildingId: string): string {
        return buildingOptions.find((o) => o.value === buildingId)?.label ?? buildingId.slice(0, 8);
    }

    function handleOpenCreate() {
        setCreateName(realEstateAsset.name);
        setCreateOpen(true);
    }

    async function handleCreate() {
        setIsCreating(true);
        try {
            const created = await ensanSteckbriefService.createSteckbrief({
                realEstateAssetId: realEstateAsset.id,
                // ALL of the REA's buildings — the create contract requires a
                // non-empty subset, and the subset is adjusted afterwards on
                // the map in the Variant form (GEN-766).
                buildingIds: realEstateAsset.buildings.map((building) => building.id),
                // Blank name → omit; the backend defaults it to the REA name.
                ...(createName.trim() ? { name: createName.trim() } : {}),
            });
            dispatch(showSnackbar({ message: t('Steckbrief created.'), severity: NotificationSeverity.Success }));
            setCreateOpen(false);
            // Straight into the Variant form — the created Steckbrief is only
            // useful once its first Variant is being filled in.
            navigate(steckbriefVariantNewUrl(realEstateAsset.id, created.id));
        } catch (err) {
            dispatch(
                showSnackbar({
                    message: isProblemDetailTextOr(err, t('Failed to create Steckbrief.')),
                    severity: NotificationSeverity.Error,
                }),
            );
        } finally {
            setIsCreating(false);
        }
    }

    async function handleDelete() {
        if (!deleteCandidate) return;
        setIsDeleting(true);
        try {
            await ensanSteckbriefService.deleteSteckbrief(deleteCandidate.id);
            dispatch(showSnackbar({ message: t('Steckbrief deleted.'), severity: NotificationSeverity.Success }));
            setDeleteCandidate(null);
            reload();
        } catch (err) {
            dispatch(
                showSnackbar({
                    message: isProblemDetailCode(err, 'STECKBRIEF_HAS_GENERATED_DOCUMENTS')
                        ? t(
                              'This Steckbrief has generated documents and cannot be deleted. The generated prints are audit records.',
                          )
                        : isProblemDetailTextOr(err, t('Failed to delete Steckbrief.')),
                    severity: NotificationSeverity.Error,
                }),
            );
        } finally {
            setIsDeleting(false);
        }
    }

    async function handleDeleteVariant() {
        if (!deleteVariantCandidate) return;
        setIsDeletingVariant(true);
        try {
            await ensanSteckbriefService.deleteVariant(
                deleteVariantCandidate.steckbrief.id,
                deleteVariantCandidate.variant.id,
            );
            dispatch(showSnackbar({ message: t('Variant deleted.'), severity: NotificationSeverity.Success }));
            setDeleteVariantCandidate(null);
            reload();
        } catch (err) {
            dispatch(
                showSnackbar({
                    message: isProblemDetailTextOr(err, t('Failed to delete the variant.')),
                    severity: NotificationSeverity.Error,
                }),
            );
        } finally {
            setIsDeletingVariant(false);
        }
    }

    /**
     * Link to a Variant's generated document — nothing when no document was
     * generated yet: generation lives in the Variant form page (GEN-765/766).
     */
    function renderVariantDocumentLink(variant: SteckbriefVariantDto) {
        if (!variant.documentUrl) return null;
        return (
            <Link
                href={variant.documentUrl}
                target="_blank"
                rel="noopener"
                sx={{ display: 'inline-flex', alignItems: 'center' }}
            >
                <OpenInNew fontSize="inherit" sx={{ mr: 0.25 }} />
                {t('Open document')}
            </Link>
        );
    }

    function renderVariantList(steckbrief: SteckbriefDto) {
        const variants = variantsBySteckbriefId[steckbrief.id] ?? [];
        if (variants.length === 0) {
            return (
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                    {t('No variants have been saved for this Steckbrief yet.')}
                </Typography>
            );
        }
        return (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t('Doc. no.')}</TableCell>
                        <TableCell>{t('Name')}</TableCell>
                        <TableCell>{t('Kind')}</TableCell>
                        <TableCell>{t('Created by')}</TableCell>
                        <TableCell>{t('Created at')}</TableCell>
                        <TableCell>{t('Document')}</TableCell>
                        <TableCell align="right" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {variants.map((variant) => (
                        <TableRow key={variant.id}>
                            {/* GEN-786: the Dok-Nr. the PDF prints — matches a printed
                                Steckbrief to its Variant. */}
                            <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                {variantDocumentNumber(steckbrief.code, variant.number) ?? '–'}
                            </TableCell>
                            <TableCell sx={{ wordBreak: 'break-word' }}>{variant.name}</TableCell>
                            <TableCell>
                                <Chip size="small" variant="outlined" label={variant.kind} />
                            </TableCell>
                            <TableCell>{variant.createdBy}</TableCell>
                            <TableCell>{asGermanDateTime(variant.createdAt)}</TableCell>
                            <TableCell>{renderVariantDocumentLink(variant)}</TableCell>
                            <TableCell align="right">
                                <Tooltip title={t('Open form')}>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            navigate(
                                                steckbriefVariantOpenUrl(realEstateAsset.id, steckbrief.id, variant.id),
                                            )
                                        }
                                        aria-label={t('Open form of variant {{name}}', { name: variant.name })}
                                    >
                                        <EditNote fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <IconButton
                                    size="small"
                                    onClick={() => setDeleteVariantCandidate({ steckbrief, variant })}
                                    aria-label={t('Delete variant {{name}}', { name: variant.name })}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (loadError) {
        return <Alert severity="error">{loadError}</Alert>;
    }

    return (
        <Stack spacing={1.5}>
            <Box>
                <Button variant="outlined" size="small" onClick={handleOpenCreate}>
                    {t('Create Steckbrief')}
                </Button>
            </Box>

            {steckbriefe.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('No Steckbriefe have been created for this real estate asset yet.')}
                </Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 40 }} />
                            <TableCell>{t('Code')}</TableCell>
                            <TableCell>{t('Name')}</TableCell>
                            <TableCell align="right">{t('Buildings')}</TableCell>
                            <TableCell align="right">{t('Variants')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {steckbriefe.map((steckbrief) => {
                            const expanded = expandedSteckbriefId === steckbrief.id;
                            return (
                                <Fragment key={steckbrief.id}>
                                    <TableRow>
                                        <TableCell sx={{ width: 40 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setExpandedSteckbriefId(expanded ? null : steckbrief.id)}
                                                aria-label={t('Variants of Steckbrief {{code}}', {
                                                    code: steckbrief.code,
                                                })}
                                            >
                                                {expanded ? (
                                                    <ExpandLess fontSize="small" />
                                                ) : (
                                                    <ExpandMore fontSize="small" />
                                                )}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{steckbrief.code}</TableCell>
                                        <TableCell sx={{ wordBreak: 'break-word' }}>{steckbrief.name}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip
                                                title={
                                                    <Stack spacing={0.25}>
                                                        {steckbrief.buildingIds.map((id) => (
                                                            <span key={id}>{buildingLabel(id)}</span>
                                                        ))}
                                                    </Stack>
                                                }
                                            >
                                                <span>{steckbrief.buildingIds.length}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            {(variantsBySteckbriefId[steckbrief.id] ?? []).length}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={t('New variant')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        navigate(
                                                            steckbriefVariantNewUrl(realEstateAsset.id, steckbrief.id),
                                                        )
                                                    }
                                                    aria-label={t('New variant for Steckbrief {{code}}', {
                                                        code: steckbrief.code,
                                                    })}
                                                >
                                                    <PostAdd fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <IconButton
                                                size="small"
                                                onClick={() => setDeleteCandidate(steckbrief)}
                                                aria-label={t('Delete Steckbrief {{code}}', { code: steckbrief.code })}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                                            <Collapse in={expanded} unmountOnExit>
                                                <Box sx={{ pl: 5, pr: 1, py: 0.5 }}>
                                                    {renderVariantList(steckbrief)}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            {/* Create dialog — name only since GEN-766 (the building subset starts
                as ALL REA buildings and is adjusted on the map in the Variant
                form). It stays a DIALOG on purpose although one field barely
                warrants one: creating mints the printed SB-NNN code, so the
                explicit confirm gesture guards against accidental codes. */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('Create Steckbrief')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                        {/* The name prints as the Steckbrief title (layout v4, GEN-775) —
                            one line, hence the print limit; blank defaults to the REA name. */}
                        <TextField
                            label={t('Name')}
                            size="small"
                            fullWidth
                            autoFocus
                            value={createName}
                            onChange={(e) => setCreateName(e.target.value)}
                            disabled={isCreating}
                            helperText={`${createName.length}/${PRINT_LIMIT_STECKBRIEF_NAME}`}
                            slotProps={{ htmlInput: { maxLength: PRINT_LIMIT_STECKBRIEF_NAME } }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={isCreating}>
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={isCreating || realEstateAsset.buildings.length === 0}
                    >
                        {t('Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Steckbrief delete confirmation */}
            <Dialog open={deleteCandidate !== null} onClose={() => setDeleteCandidate(null)}>
                <DialogTitle>{t('Delete Steckbrief?')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t(
                            'Are you sure you want to delete Steckbrief "{{name}}" ({{code}})? All of its variants are deleted with it. This cannot be undone.',
                            { name: deleteCandidate?.name ?? '', code: deleteCandidate?.code ?? '' },
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteCandidate(null)} disabled={isDeleting}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting}>
                        {t('Delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Variant delete confirmation — a soft delete (GEN-763): the row,
                its generated documents and the data stamp survive server-side. */}
            <Dialog open={deleteVariantCandidate !== null} onClose={() => setDeleteVariantCandidate(null)}>
                <DialogTitle>{t('Delete variant?')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t(
                            'Are you sure you want to delete variant "{{name}}"? It disappears from this list; generated documents and their data stamp are preserved.',
                            { name: deleteVariantCandidate?.variant.name ?? '' },
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteVariantCandidate(null)} disabled={isDeletingVariant}>
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteVariant}
                        disabled={isDeletingVariant}
                    >
                        {t('Delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
